import {
  W, H, COLS, PREVIEW_Y, THINK_DELAY, STATS_KEY,
  GameState, View, Falling, Stats, Player,
} from './types';
import { createState, applyMove, dropRow } from './game';
import { bestMove } from './ai';
import { setupCanvas, readTheme, renderFrame, cellY } from './renderer';
import { attachInput, canHover } from './input';
import { preloadAudio, resumeAudio, playDrop, playLand, playWin, playLose } from './audio';
import { track } from './analytics';

const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx = setupCanvas(canvas);
const theme = readTheme();

// Fit the canvas into the viewport preserving the 480:600 aspect (letterboxed).
function fitCanvas(): void {
  const scale = Math.min(window.innerWidth / W, window.innerHeight / H);
  canvas.style.width = Math.round(W * scale) + 'px';
  canvas.style.height = Math.round(H * scale) + 'px';
}
fitCanvas();
window.addEventListener('resize', fitCanvas);
window.addEventListener('orientationchange', fitCanvas);
preloadAudio();

function loadStats(): Stats {
  try {
    const raw = localStorage.getItem(STATS_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      return { wins: s.wins | 0, losses: s.losses | 0, draws: s.draws | 0 };
    }
  } catch { /* ignore */ }
  return { wins: 0, losses: 0, draws: 0 };
}
function saveStats(stats: Stats): void {
  try { localStorage.setItem(STATS_KEY, JSON.stringify(stats)); } catch { /* ignore */ }
}

let state: GameState = createState(loadStats());
let previewCol: number | null = canHover ? 3 : null;
let falling: Falling | null = null;
let fallStart = 0;
let fallDur = 0;
let botThinkStart = -1;     // timestamp the bot started "thinking" (-1 = not)
let shakeStart = -Infinity;
let menuOpen = false;       // ☰ pause overlay (phase stays 'playing' while paused)
let pauseStart = 0;         // performance.now() at the moment menuOpen became true, for grace-period shift on resume

function setMenuOpen(open: boolean): void {
  if (open === menuOpen) return;
  if (open) {
    pauseStart = performance.now();
  } else {
    const pausedMs = performance.now() - pauseStart;
    if (falling) fallStart += pausedMs;
    if (botThinkStart >= 0) botThinkStart += pausedMs;
  }
  menuOpen = open;
}

const canInput = (): boolean =>
  state.phase === 'playing' && state.currentPlayer === 'human' && !falling && !menuOpen;

function startGame(): void {
  state = { ...createState(state.stats), phase: 'playing', currentPlayer: 'human' };
  track('game_start');
  previewCol = canHover ? 3 : null;
  falling = null;
  botThinkStart = -1;
  menuOpen = false;
  pauseStart = 0;
  resumeAudio();
}

function beginFall(col: number, player: Player, now: number): void {
  const row = dropRow(state.board, col);
  if (row < 0) return;
  const targetY = cellY(row);
  falling = { col, row, player, y: PREVIEW_Y };
  fallStart = now;
  fallDur = Math.max(250, Math.min(450, 250 + (targetY - PREVIEW_Y) * 0.4));
  resumeAudio();
  playDrop();
}

function settle(now: number): void {
  const f = falling!;
  falling = null;
  state = applyMove(state, f.col);    // places for state.currentPlayer, switches turn
  playLand();

  if (state.phase === 'won') { state.winner === 'human' ? playWin() : playLose(); saveStats(state.stats); menuOpen = false; track('game_over', { result: state.winner === 'human' ? 'win' : 'lose' }); }
  else if (state.phase === 'draw') { saveStats(state.stats); menuOpen = false; track('game_over', { result: 'draw' }); }
  else if (state.currentPlayer === 'bot') { botThinkStart = now; }   // bot's turn next
}

// human-initiated drop (tap / drag-release / keyboard)
function humanDrop(col: number): void {
  if (!canInput()) return;
  if (dropRow(state.board, col) < 0) { shakeStart = performance.now(); return; }
  beginFall(col, 'human', performance.now());
}

attachInput(canvas, {
  getPhase: () => state.phase,
  canInput,
  isMenuOpen: () => menuOpen,
  hoverCol: (col) => { previewCol = col; },
  dragCol: (col) => { previewCol = col; },
  release: (col) => {
    if (col != null) humanDrop(col);
    if (!canHover) previewCol = null;   // mobile: no persistent highlight
  },
  pressButton: (name) => {
    if (name === 'play' || name === 'again' || name === 'restart') startGame();
    else if (name === 'menu') { state = { ...state, phase: 'menu' }; }
    else if (name === 'pause') { setMenuOpen(true); }
    else if (name === 'resume') { setMenuOpen(false); }
    else if (name === 'hub') { location.href = document.body.dataset.hubUrl ?? '/'; }
  },
  keyMove: (dir) => { previewCol = Math.max(0, Math.min(COLS - 1, (previewCol ?? 3) + dir)); },
  keyDrop: () => { humanDrop(previewCol ?? 3); },
  keyRestart: () => { if (state.phase !== 'menu') startGame(); },
});

document.addEventListener('touchstart', resumeAudio, { once: true });
document.addEventListener('click', resumeAudio, { once: true });
document.addEventListener('visibilitychange', () => { if (!document.hidden) resumeAudio(); });

function fallingY(now: number): number {
  const f = falling!;
  const targetY = cellY(f.row);
  const t = Math.min((now - fallStart) / fallDur, 1);
  let y = PREVIEW_Y + (targetY - PREVIEW_Y) * (t * t);   // gravity-like ease-in
  if (t > 0.9) y -= Math.sin((t - 0.9) / 0.1 * Math.PI) * 7;  // small landing bounce
  return y;
}

function frame(now: number): void {
  try {
    if (state.phase !== 'playing') menuOpen = false;   // can't stick outside an active game

    if (!menuOpen) {
      if (falling) {
        if (now - fallStart >= fallDur) settle(now);
        else falling.y = fallingY(now);
      }

      // bot turn: think for THINK_DELAY then drop
      if (botThinkStart >= 0 && !falling && state.phase === 'playing' && state.currentPlayer === 'bot') {
        if (now - botThinkStart >= THINK_DELAY) {
          const col = bestMove(state.board);
          botThinkStart = -1;
          beginFall(col, 'bot', now);
        }
      }
    }

    const view: View = {
      now,
      previewCol: canInput() ? previewCol : null,
      falling,
      botThinking: state.phase === 'playing' && state.currentPlayer === 'bot',
      shakeMs: now - shakeStart,
    };
    renderFrame(ctx, state, theme, view, menuOpen);
  } catch (e) {
    console.error('[connect4] loop error', e);
  }
  requestAnimationFrame(frame);
}

requestAnimationFrame(frame);
